import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Popover, Row } from 'antd';
import { useGetTotalInPutSummary } from 'src/api/usageFunction';
import { formatNumberWithCommas } from 'src/utils/numberUtils';
import Spin from 'src/components/common/Spin';
import styles from './UsageInputs.module.less';

const InfoPopoverContent = ({ content }: { content: string }) => <div className={styles.topInfoBox}>{content}</div>;

type UsageInputsProps = {
  label: string;
  content?: string | React.ReactNode;
  popoverContent: string;
};

const UsageInputBlock: React.FC<UsageInputsProps> = ({ label, content, popoverContent }) => (
  <div className={styles.usageInputBlock}>
    <Popover content={<InfoPopoverContent content={popoverContent} />} trigger="hover" placement="top">
      <FontAwesomeIcon icon={['fal', 'info-circle']} className={styles.infoIcons} />
    </Popover>
    <h2>{label}</h2>
    {content && <h1 className={styles.headingWeight}>{content}</h1>}
  </div>
);

type UsageInputSummaryProps = {
  application: string;
  date?: {
    startDate: string;
    endDate: string;
  };
  usageType: string;
  site: string;
  user: string;
  timeZone: string;
};
const UsageInputSummary: React.FC<UsageInputSummaryProps> = ({
  application,
  site,
  user,
  date,
  usageType,
  timeZone,
}) => {
  const [requestParams, setRequestParams] = useState({
    appId: application,
    usageType,
    startDate: date?.startDate || '',
    endDate: date?.endDate || '',
    siteCode: site,
    accountId: user,
    timeZone,
  });
  const [loading, totalInPutSummary] = useGetTotalInPutSummary(requestParams);

  useEffect(() => {
    setRequestParams({
      appId: application,
      usageType,
      startDate: date?.startDate || '',
      endDate: date?.endDate || '',
      siteCode: site,
      accountId: user,
      timeZone,
    });
  }, [application, date, usageType, site, user, timeZone]);

  const getContent = (value: string | undefined, isLoading: boolean) => {
    if (isLoading) {
      return <Spin spinning={isLoading} />;
    } else {
      return value ?? <p className={styles.paraContent}>No data available</p>;
    }
  };

  return (
    <div>
      <Row gutter={16}>
        <Col span={12} className={styles.firstUsageInputBlock}>
          <div className={styles.usageInputBox}>
            <UsageInputBlock
              label={usageType === 'output' ? 'Total Outputs' : 'Total Inputs'}
              content={getContent(formatNumberWithCommas(totalInPutSummary?.totalInput), loading)}
              popoverContent={`Total ${usageType === 'output' ? 'Outputs' : 'Inputs'} based on the filter criteria`}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className={styles.usageInputBox}>
            <UsageInputBlock
              label={`Total Executed ${usageType === 'output' ? 'Outputs' : 'Inputs'}`}
              content={getContent(formatNumberWithCommas(totalInPutSummary?.totalExecuted), loading)}
              popoverContent={`Total Executed ${
                usageType === 'output' ? 'Outputs' : 'Inputs'
              } based on the filter criteria`}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default UsageInputSummary;
